import { useState, useEffect, useCallback } from 'react';
import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { refresh, every, type Payloads, type WriteHook, type WriteResult } from '@signalvote/data';
import { ApiErrorHandler } from '@/utils/apiErrorHandler';
import { API_BASE_URL } from '@/config';
import { queryKeys } from '@/lib/queryKeys';
import { campaignHeaders, defineQuery, defineWrite } from '@/lib/data';
import { useImportProgressStore } from '@/stores/importProgressStore';
import { useVoter, useVoterFilterOptions, useVoterMapPoints, useVoters } from './useVoters';
import type {
  ImportHistoryResponse,
  ImportJobRecordsResponse,
  GeocodingStatsResponse,
  RequestUploadRequest,
  RetryImportRequest,
  StagingTableInfo,
  VoterImportCancelResponse,
  VoterImportProgressEvent,
  VoterImportRegeocodeResponse,
  VoterImportRetryResponse,
  VoterImportStagingRowUpdateResponse,
  VoterImportStartedResponse,
  VoterImportUploadResponse,
} from '@signalvote/shared';

// ============================================================================
// Query Hooks
// ============================================================================

function pageQuery(params: Record<string, string | number | undefined> | undefined): string {
  const searchParams = new URLSearchParams();
  for (const [key, value] of Object.entries(params ?? {})) if (value) searchParams.set(key, String(value));
  const qs = searchParams.toString();
  return qs ? `?${qs}` : '';
}

/** Import job history with pagination. */
export const useImportHistory = defineQuery({
  key: (params: { page?: number; pageSize?: number } | undefined) => ['voterImport', 'history', params],
  reads: 'GET /api/VoterImport/history',
  fetch: (params, { signal, campaignId }): Promise<ImportHistoryResponse> =>
    ApiErrorHandler.makeAuthenticatedRequest(`${API_BASE_URL}/api/voterimport/history${pageQuery(params)}`, {
      signal,
      headers: campaignHeaders(campaignId),
    }),
});

/** Campaign-wide geocoding accuracy stats. */
export const useGeocodingStats = defineQuery({
  key: () => ['voterImport', 'geocodingStats'],
  reads: 'GET /api/VoterImport/geocoding-stats',
  fetch: (_: undefined, { signal, campaignId }): Promise<GeocodingStatsResponse> =>
    ApiErrorHandler.makeAuthenticatedRequest(`${API_BASE_URL}/api/voterimport/geocoding-stats`, {
      signal,
      headers: campaignHeaders(campaignId),
    }),
});

/**
 * Fetch the columns of an existing staging table so its mapping can be redone.
 * Keyed on the table name so a response for a table the user has navigated away
 * from is never applied to the one they are looking at now.
 */
export function useStagingTableInfo(stagingTableName?: string) {
  return useQuery({
    queryKey: queryKeys.voterImport.stagingTable(stagingTableName ?? ''),
    queryFn: (): Promise<StagingTableInfo> =>
      ApiErrorHandler.makeAuthenticatedRequest(
        `${API_BASE_URL}/api/voterimport/staging-tables/${stagingTableName}`
      ),
    enabled: !!stagingTableName,
  });
}

export interface ImportJobRecordsParams {
  jobId: string;
  status?: string;
  page?: number;
  pageSize?: number;
}

/** One import job's records, with optional filters. */
export const useImportJobRecords = defineQuery({
  key: ({ jobId, ...filters }: ImportJobRecordsParams) => ['voterImport', 'records', jobId, filters],
  reads: 'GET /api/VoterImport/{jobId}/records',
  fetch: ({ jobId, ...filters }, { signal, campaignId }): Promise<ImportJobRecordsResponse> =>
    ApiErrorHandler.makeAuthenticatedRequest(`${API_BASE_URL}/api/voterimport/${jobId}/records${pageQuery(filters)}`, {
      signal,
      headers: campaignHeaders(campaignId),
    }),
});

// ============================================================================
// SSE Hooks
// ============================================================================

/**
 * Subscribe to real-time import progress via Server-Sent Events.
 * Reconnects on connection drop (3s delay), but NOT after terminal
 * states (Completed/Failed/Cancelled) or when backend reports Idle (no active job).
 *
 * @param reconnectKey — change this value to force a fresh SSE connection
 *   (e.g., after kicking off a new import).
 * @param enabled — false for a user the API refuses voter import, so no stream is opened.
 */
export function useImportProgress(reconnectKey = 0, enabled = true) {
  const [progress, setProgress] = useState<VoterImportProgressEvent | null>(null);
  const [connected, setConnected] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    const controller = new AbortController();
    let terminated = false;
    let retryCount = 0;
    const MAX_RETRIES = 5;

    async function connect() {
      if (terminated) return;

      try {
        const response = await ApiErrorHandler.makeAuthenticatedRequestRaw(
          `${API_BASE_URL}/api/voterimport/progress-stream`,
          { signal: controller.signal }
        );

        if (!response.ok || !response.body) return;

        setConnected(true);
        retryCount = 0; // Reset on successful connection
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() ?? '';

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const data = JSON.parse(line.slice(6)) as VoterImportProgressEvent;
                // Backend sends {"status":"Idle"} when no active job exists
                if (data.status === 'Idle') {
                  terminated = true;
                  break;
                }
                setProgress(data);
                // Don't reconnect after terminal states
                if (data.status === 'Completed' || data.status === 'Failed' || data.status === 'Cancelled') {
                  terminated = true;
                }
              } catch { /* ignore parse errors */ }
            }
          }
        }
      } catch (err) {
        if ((err as Error).name !== 'AbortError' && !terminated) {
          retryCount++;
          if (retryCount <= MAX_RETRIES) {
            // Exponential backoff: 3s, 6s, 12s, 24s, 48s
            const delay = 3000 * Math.pow(2, retryCount - 1);
            setTimeout(connect, delay);
          }
        }
      } finally {
        setConnected(false);
      }
    }

    connect();
    return () => controller.abort();
  }, [reconnectKey, enabled]);

  return { progress, connected };
}

// ============================================================================
// Mutation Hooks
// ============================================================================

/**
 * The import, cancel, retry and re-geocode actions write their job rows from a child DI scope, whose
 * tenant context carries its own write set, so the response's `X-SV-Writes` never names them. Until
 * the server names them, each refreshes the import reads it changed once it succeeds.
 */
function refreshingAfter<I, R>(useWrite: WriteHook<I, R, Payloads>, after: (client: QueryClient) => void): WriteHook<I, R, Payloads> {
  return function useRefreshingWrite() {
    const client = useQueryClient();
    const write = useWrite();
    const { mutate: send, mutateAsync: sendAsync } = write;
    const mutate = useCallback<WriteResult<I, R, Payloads>['mutate']>(
      (input, options) =>
        send(input, {
          ...options,
          onSuccess: (...settled) => {
            after(client);
            return options?.onSuccess?.(...settled);
          },
        }),
      [send, client],
    );
    const mutateAsync = useCallback<WriteResult<I, R, Payloads>['mutateAsync']>(
      async (input, options) => {
        const result = await sendAsync(input, options);
        after(client);
        return result;
      },
      [sendAsync, client],
    );
    return { ...write, mutate, mutateAsync };
  };
}

const refreshHistory = (client: QueryClient) => void refresh(client, useImportHistory, every);

/**
 * For the import progress stream reaching a terminal state: the background job wrote the voters
 * and its job row with no request this client made, so no write set names them.
 */
export function refreshImportedVoters(client: QueryClient): void {
  void refresh(client, useVoters, every);
  void refresh(client, useVoter, every);
  void refresh(client, useVoterMapPoints, every);
  void refresh(client, useVoterFilterOptions, every);
  void refresh(client, useGeocodingStats, every);
  refreshHistory(client);
}

/** Request a presigned upload URL for a voter import file. */
export const useRequestUpload = defineWrite({
  send: (fileName: string): Promise<VoterImportUploadResponse> =>
    ApiErrorHandler.makeAuthenticatedRequest(`${API_BASE_URL}/api/voterimport/request-upload`, {
      method: 'POST',
      body: JSON.stringify({ fileName } satisfies RequestUploadRequest),
    }),
});

/** Start an import job from a file already uploaded to Spaces. */
export const useImportFromSpaces = refreshingAfter(
  defineWrite({
    send: (request: {
      fileKey: string;
      fileName: string;
      delimiter: string;
      mapping: Record<string, unknown>;
    }): Promise<VoterImportStartedResponse> =>
      ApiErrorHandler.makeAuthenticatedRequest(`${API_BASE_URL}/api/voterimport/import`, {
        method: 'POST',
        body: JSON.stringify(request),
      }),
  }),
  refreshHistory,
);

/** Cancel an in-progress import job. */
export const useCancelImport = refreshingAfter(
  defineWrite({
    send: (jobId: string): Promise<VoterImportCancelResponse> =>
      ApiErrorHandler.makeAuthenticatedRequest(`${API_BASE_URL}/api/voterimport/${jobId}/cancel`, { method: 'POST' }),
  }),
  refreshHistory,
);

/** Retry failed rows in an import job. */
export const useRetryImport = refreshingAfter(
  defineWrite({
    send: ({ jobId, rowNumbers }: { jobId: string; rowNumbers: number[] }): Promise<VoterImportRetryResponse> =>
      ApiErrorHandler.makeAuthenticatedRequest(`${API_BASE_URL}/api/voterimport/${jobId}/retry`, {
        method: 'POST',
        body: JSON.stringify({ rowNumbers } satisfies RetryImportRequest),
      }),
  }),
  (client) => {
    void refresh(client, useImportJobRecords, every);
    refreshHistory(client);
  },
);

/** Update a single staging row before import. */
export const useUpdateStagingRow = defineWrite({
  send: ({
    tableName,
    rowId,
    data,
  }: {
    tableName: string;
    rowId: number;
    data: Record<string, string>;
  }): Promise<VoterImportStagingRowUpdateResponse> =>
    ApiErrorHandler.makeAuthenticatedRequest(`${API_BASE_URL}/api/voterimport/staging-rows/${tableName}/${rowId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
});

/** Trigger re-geocoding of low-accuracy voters. */
export const useRegeocodeVoters = refreshingAfter(
  defineWrite({
    send: (_: void): Promise<VoterImportRegeocodeResponse> =>
      ApiErrorHandler.makeAuthenticatedRequest(`${API_BASE_URL}/api/voterimport/regeocode`, {
        method: 'POST',
        body: JSON.stringify({}),
      }),
    onSuccess: () => useImportProgressStore.getState().startRegeocode(),
  }),
  (client) => {
    refreshHistory(client);
    void refresh(client, useGeocodingStats, every);
  },
);

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Returns a function that downloads import errors as a CSV file.
 * Not a hook - call the returned function to trigger the download.
 */
export function createExportImportErrors(jobId: string) {
  const exportErrors = async () => {
    const response = await ApiErrorHandler.makeAuthenticatedRequestRaw(
      `${API_BASE_URL}/api/voterimport/${jobId}/export-errors`
    );

    if (!response.ok) {
      throw new Error('Failed to export errors');
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `import-errors-${jobId}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  return exportErrors;
}
